/**
 * Auth Store - State Management для аутентификации
 * Аналог lib/features/auth/presentation/bloc/auth_bloc.dart
 *
 * Состояния: Unauthenticated, Loading, Authenticated, Error
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '../api/auth.api'
import type {
  AcceptInvitationPayload,
  AuthState,
  LoginRequest,
  LoginResponse,
  User,
  UserSettings,
} from '../types/auth.types'
import { useRouter } from 'vue-router'
import dayjs from '@/utils/dayjs'
import { i18n } from '@/i18n'
import { clearListQueries } from '@/utils/listQueryMemory'
import { clearChatDrafts } from '@/utils/chatDrafts'
import { loginErrorText } from '../utils/loginError'
import { safeRedirect } from '../utils/loginRedirect'
import { isSessionRejected } from '@/api/interceptors/auth.interceptor'
import { isImpersonating, readImpersonation, tokenStorage } from '../tokenStorage'
import { useSectionsStore } from './sections.store'
import { useManagersStore } from '@/stores/managers'
import { clearDealNames } from '@/stores/dealNames'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  // State
  const user = ref<User | null>(null)
  const userSettings = ref<UserSettings | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshTokenValue = ref<string | null>(null)
  const authState = ref<AuthState>('unauthenticated')
  // Сеанс просмотра «глазами пользователя» этой вкладки (DEV-2399); null — обычная
  // вкладка. Ref, а не голое значение: storeToRefs падает на null-свойстве стора.
  const impersonation = ref(readImpersonation())
  const error = ref<string | null>(null)
  // DEV-2490. Проверка сессии на старте не дошла до сервера (сеть, 5xx): токены
  // целы, гард показывает «Нет связи · Повторить», а не форму входа.
  const connectionLost = ref(false)

  // Getters
  const isAuthenticated = computed(
    () => authState.value === 'authenticated' && !!accessToken.value && !!user.value,
  )

  const isLoading = computed(() => authState.value === 'loading')

  // Таймзона менеджера: профиль → guess браузера → UTC.
  const userTimezone = computed(() => {
    const tz = userSettings.value?.timezone
    if (tz) return tz
    try {
      return dayjs.tz.guess() || 'UTC'
    } catch {
      return 'UTC'
    }
  })

  // DEV-2503. Сессия во вкладке: на старте настройки грузятся параллельно с /me —
  // ответ, доехавший после выхода или входа другим, уже чужой.
  let session = 0

  const loadUserSettings = async () => {
    const mine = session
    try {
      const settings = await authApi.getUserSettings()
      if (mine === session) userSettings.value = settings
    } catch (err) {
      console.error('Failed to load user settings:', err)
    }
  }

  // Actions

  /**
   * applyLoginResponse — общий хвост входа: сессия, localStorage, настройки и
   * переход на дашборд. Один на пароль и на Google (DEV-2119): две копии этих
   * семи строк рано или поздно разъехались бы, и вход одним способом начал бы
   * отличаться от входа другим.
   *
   * redirect — экран, с которого гард увёл на вход (DEV-2490): менеджер жмёт
   * ссылку на лида из Telegram-бота и после входа должен попасть на лида.
   */
  const applyLoginResponse = async (response: LoginResponse, redirect?: unknown) => {
    session += 1
    // DEV-2503. Карта разделов могла остаться от прежнего пользователя: с параллельным
    // стартом /sections уходит до проверки /me («Нет связи» после ответа карты).
    useSectionsStore().invalidate()
    useManagersStore().invalidate()
    clearDealNames()
    // DEV-2502. Черновики чатов прошлой сессии этой вкладки — не новому пользователю: сессия
    // могла кончиться и мимо clearSession (истёкший токен, перехватчик).
    clearChatDrafts()
    // Save tokens
    accessToken.value = response.tokens.accessToken
    refreshTokenValue.value = response.tokens.refreshToken
    user.value = response.user
    // Права живут только в памяти — как и карта разделов. В localStorage
    // попадает косметика (имя, почта, аватар, роли для ярлыка), и вход
    // другим пользователем в той же вкладке не наследует чужой доступ.
    useSectionsStore().setPermissions(response.user.permissions)
    authState.value = 'authenticated'
    // Вошёл заново после сбоя связи на старте — «Нет связи» больше не про него.
    connectionLost.value = false

    // Persist to localStorage
    tokenStorage.setTokens(response.tokens.accessToken, response.tokens.refreshToken)
    tokenStorage.setUser(response.user)

    // Пояс из профиля — до первого экрана: «на сегодня» в задачах и фильтрах сделок
    // иначе посчитался бы в поясе браузера.
    await loadUserSettings()

    router.push(safeRedirect(redirect) ?? '/dashboard')
  }

  /**
   * Login - аналог LoginUser usecase
   */
  const login = async (credentials: LoginRequest, redirect?: unknown) => {
    authState.value = 'loading'
    error.value = null

    try {
      await applyLoginResponse(await authApi.login(credentials), redirect)
    } catch (err: unknown) {
      authState.value = 'error'
      // DEV-1775: человеку — причина отказа, а не «Request failed with status code 403».
      error.value = loginErrorText(err, (key) => i18n.global.t(key))
      throw err
    }
  }

  /**
   * loginWithSSOCode — обмен одноразового кода, полученного после входа через
   * Google, на сессию (DEV-2119).
   *
   * Код одноразовый и живёт минуту, поэтому повторный заход по той же ссылке
   * возврата законно проваливается. Ошибку здесь не переводим в текст формы:
   * экран возврата уводит на /login с внятной причиной сам.
   */
  const loginWithSSOCode = async (code: string, redirect?: unknown) => {
    authState.value = 'loading'
    error.value = null

    try {
      await applyLoginResponse(await authApi.exchangeSSOCode(code), redirect)
    } catch (err: unknown) {
      authState.value = 'unauthenticated'
      throw err
    }
  }

  /**
   * Logout - аналог LogoutUser usecase
   *
   * redirect: false нужен вызову из навигационного гварда — там переход задаёт
   * сам гвард через next(), и router.push() отсюда отменил бы его как
   * конкурирующую навигацию (DEV-1177).
   */
  const logout = async (options: { redirect?: boolean } = {}) => {
    const { redirect = true } = options

    // Во вкладке просмотра «выйти» — значит закончить просмотр: сеанс самого
    // пользователя на его устройствах трогать нельзя (DEV-2399).
    if (isImpersonating()) {
      await endImpersonation()
      return
    }

    try {
      await authApi.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearSession()

      if (redirect) {
        router.push('/login')
      }
    }
  }

  /** Забывает сессию в этой вкладке. Сервер не трогает — это делает logout(). */
  const clearSession = () => {
    session += 1
    user.value = null
    userSettings.value = null
    accessToken.value = null
    refreshTokenValue.value = null
    authState.value = 'unauthenticated'
    error.value = null

    tokenStorage.clear()
    // DEV-2392. Последние фильтры списков (в поиске бывают ПДн клиентов).
    clearListQueries()
    // DEV-2502. Черновики чатов — следующему в этой вкладке они не достанутся.
    clearChatDrafts()

    // DEV-1253. Карта разделов живёт в памяти и перезагрузку страницы при
    // смене учётной записи не переживает — но вход в той же вкладке её бы
    // переживал, и второй пользователь получил бы разделы первого.
    useSectionsStore().invalidate()
    // DEV-2503. Справочник менеджеров тоже на вкладку, и состав зависит от прав; имена
    // клиентов сделок — данные прежней сессии.
    useManagersStore().invalidate()
    clearDealNames()
  }

  /**
   * Refresh Token - аналог RefreshToken usecase
   */
  const refreshToken = async () => {
    if (!refreshTokenValue.value) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await authApi.refreshToken(refreshTokenValue.value)

      accessToken.value = response.tokens.accessToken
      refreshTokenValue.value = response.tokens.refreshToken

      // Update localStorage
      tokenStorage.setTokens(response.tokens.accessToken, response.tokens.refreshToken)
    } catch (err) {
      // Refresh failed - logout
      await logout()
      throw err
    }
  }

  /**
   * setTokens — синхронная установка пары токенов. Вызывается из axios-интерсептора
   * после фонового refresh, чтобы реактивный стор не отставал от localStorage
   * (accessToken читают напрямую, напр. usePresentationDownload).
   */
  const setTokens = (newAccessToken: string, newRefreshToken: string) => {
    accessToken.value = newAccessToken
    refreshTokenValue.value = newRefreshToken
  }

  /**
   * Initialize - load from localStorage (аналог AppStarted event в AuthBloc)
   */
  const initialize = async () => {
    connectionLost.value = false
    const storedAccessToken = tokenStorage.getAccessToken()
    const storedRefreshToken = tokenStorage.getRefreshToken()
    const storedUser = tokenStorage.getUser()

    // У вкладки просмотра refresh-токена нет по устройству (DEV-2399).
    if (storedAccessToken && (storedRefreshToken || isImpersonating())) {
      accessToken.value = storedAccessToken
      refreshTokenValue.value = storedRefreshToken

      // Загружаем сохранённого пользователя из localStorage
      if (storedUser) {
        try {
          user.value = JSON.parse(storedUser)
        } catch (err) {
          console.error('Failed to parse stored user:', err)
        }
      }

      authState.value = 'loading'

      // DEV-2503. /settings и /sections /me не ждут — им нужен только токен. Раньше
      // старт шёл цепочкой /me → /settings → /sections, теперь три запроса разом: ждём
      // /me и /settings (пояс из профиля нужен первому экрану — «на сегодня» в задачах и
      // фильтрах сделок), а карту разделов гард возьмёт тем же промисом стора, её ошибку
      // покажет он же.
      const settingsLoaded = loadUserSettings()
      useSectionsStore()
        .ensureLoaded()
        .catch(() => {})

      try {
        // Get current user from API and update localStorage
        const currentUser = await authApi.getCurrentUser()
        user.value = currentUser
        useSectionsStore().setPermissions(currentUser.permissions)
        tokenStorage.setUser(currentUser)
        authState.value = 'authenticated'
        await settingsLoaded
      } catch (err) {
        console.error('Failed to get current user:', err)

        if (isSessionRejected(err)) {
          // DEV-2490. Сервер отклонил сессию, и refresh её не спас. Выход на
          // сервере не зовём: токены и так мертвы, а logout гасит их на всех
          // устройствах (DEV-671). Форму входа с ?redirect= покажет гард.
          clearSession()
        } else {
          // Обрыв, таймаут или 5xx — о сессии это ничего не говорит. Раньше
          // здесь был logout(): плохая сеть на старте разлогинивала человека
          // на всех устройствах.
          authState.value = 'unauthenticated'
          connectionLost.value = true
        }
      }
    } else {
      authState.value = 'unauthenticated'
    }
  }

  /**
   * DEV-1496. Свежий снимок пользователя после 403: права могли реально
   * измениться, а карта разделов хранит их копию из /me. Ошибку глотаем —
   * прежний снимок лучше пустого, разлогинивать здесь нельзя.
   */
  const refreshUserSnapshot = async () => {
    try {
      const currentUser = await authApi.getCurrentUser()
      user.value = currentUser
      useSectionsStore().setPermissions(currentUser.permissions)
      tokenStorage.setUser(currentUser)
    } catch {
      /* сеть или мёртвая сессия — 401 обработает интерцептор */
    }
  }

  /**
   * Принятие приглашения (DEV-1308): заводит учётку по токену из письма и
   * оставляет человека внутри — сервер отдаёт пару токенов сразу, второй раз
   * логиниться незачем.
   *
   * Сессия ставится через initialize(): она уже тянет /me с правами, настройки
   * и статус. Повторять здесь семь строк из login() значило бы завести второе
   * определение того, что такое «вошёл», и первое же расхождение вылезло бы
   * пустой картой разделов.
   */
  const acceptInvitation = async (payload: AcceptInvitationPayload) => {
    const tokens = await authApi.acceptInvitation(payload)

    tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken)

    await initialize()

    router.push('/dashboard')
  }

  /**
   * Завершает просмотр «глазами пользователя» (DEV-2399): сервер гасит сеанс,
   * вкладка забывает его токен и закрывается. Если браузер закрыть её не даёт
   * (вкладку открыл не скрипт), уводим на список пользователей — там
   * загрузится сеанс администратора из localStorage.
   */
  const endImpersonation = async () => {
    try {
      await authApi.endImpersonation()
    } catch (err) {
      // Сеанс мог уже истечь — забыть токен вкладки всё равно нужно.
      console.error('End impersonation error:', err)
    } finally {
      tokenStorage.clear()
      // «Завершить» могли нажать, пока роутер ещё поднимает вкладку: его
      // replaceState в Firefox отменил бы уход, и вкладка осталась бы с чужими
      // данными, но уже с токеном администратора.
      await router.isReady().catch(() => undefined)
      window.close()
      window.location.replace('/dashboard/admin/users')
    }
  }

  /**
   * Clear error
   */
  const clearError = () => {
    error.value = null
  }

  return {
    // State
    user,
    userSettings,
    accessToken,
    authState,
    error,
    impersonation,
    connectionLost,

    // Getters
    isAuthenticated,
    isLoading,
    userTimezone,

    // Actions
    login,
    loginWithSSOCode,
    logout,
    refreshToken,
    setTokens,
    initialize,
    acceptInvitation,
    refreshUserSnapshot,
    endImpersonation,
    clearError,
    loadUserSettings,
  }
})
